import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import 'vendor/photoswipe/dist/photoswipe.css';
import 'vendor/photoswipe/dist/default-skin/default-skin.css';
import PhotoSwipe from 'vendor/photoswipe/dist/photoswipe.js';
import PhotoSwipeUI_Default from 'vendor/photoswipe/dist/photoswipe-ui-default.js';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import VideoModal from 'app/components/VideoModal';

import classes from './classes.module.scss';
import JsxContainer from './JsxContainer';

// the standard image handling requires a width and height.
// alternatively HTML templates can be used, but gestures won't work.
// fallback to HTML templates here if width and height aren't provided
const cleanItems = items => {
  return items && items.map(item => {
    if (item.html || (item.w && item.h && item.src)) {
      return item;
    }

    return {
      id: item.id,
      html: `<div style="width:90%;height:90%;margin-left:5%;background-image:url(${item.src});background-size:cover" title="${item.id}: ${item.filename}"></div>`
    }
  });
}

const usePhotoSwipeItems = props => {
  const {
    items,
    itemCount,
    itemsPerPage,
    getItems
  } = props;

  // set ready to true if items prop is passed,
  // otherwise set to false and get items
  const [ready, setReady] = useState(!!items);

  // handling bad moment counts from jobs/customers
  const effItemCount = itemCount < 0 ? 0 : itemCount

  // pages will be an array of arrays of items
  const pageCount = items ? 1 : Math.ceil(effItemCount / itemsPerPage);
  const pagesRef = useRef(new Array(pageCount));

  const pageNotLoaded = page => !pagesRef.current[page - 1];

  const loadPage = async page => {
    pagesRef.current[page - 1] = cleanItems(await getItems(page, itemsPerPage));
    return pagesRef.current[page - 1]
  }

  // enable JSX components.
  // an event listener will mount the JSX
  if (items) {
    for (let item of items) {
      if (item.jsx) {
        item.html = `<div id="pswp-jsx-${item.id}"></div>`;
      }
    }
  }



  useEffect(() => {
    if (getItems) {
      let promises = [];
      promises.push(loadPage(1));

      if (pageCount > 1) {
        promises.push(loadPage(pageCount));
      }

      Promise.all(promises).then((values) => {
        if (values && values[0].length) {
          setReady(true);
        }
      });
    }
  }, []);

  const getPageIndex = index => {
    // find the page and relative index
    let page = parseInt(index / itemsPerPage);
    let pageIndex = index - (page * itemsPerPage);

    return { page, pageIndex };
  }

  const createItemsProxy = () => new Proxy([], {
    get: (items, prop) => {
      // if prop is an array index, then get the item from the page.
      // if it's `length`, then return the itemCount.
      // otherwise ignore
      let index = parseInt(prop);

      if (!isNaN(index)) {
        let { page, pageIndex } = getPageIndex(index);

        // return the item from the page array
        return pagesRef.current[page][pageIndex];

      } else if (prop === 'length') {
        return itemCount < 0 ? 0 : itemCount;

      } else {
        return items[prop];
      }
    }
  });

  const onItemChange = index => {
    // load the previous or next page if the item is within 5 spaces of it.
    // if there is only one page then do nothing
    const { page, pageIndex } = getPageIndex(index);

    if (pageCount === 1) {
      return;
    }

    let currPage = page + 1; // page is actually the index in pages
    let prevPage = currPage - 1;
    if (prevPage === 0) {
      prevPage = pageCount;
    }

    let nearPrevPage = pageIndex < 5;
    let nearNextPage = pageIndex > itemsPerPage - 5;
    let isLastPage = currPage === pageCount;

    if (nearPrevPage && pageNotLoaded(prevPage)) {
      loadPage(prevPage);
    }

    if (nearNextPage && !isLastPage && pageNotLoaded(currPage + 1)) {
      loadPage(currPage + 1);
    }
  }

  return {
    ready,
    items: (items && cleanItems(items)) || createItemsProxy(),
    onItemChange,
    isProxy: !!getItems
  }
}

const PhotoSwipeWrapper = props => {
  const {
    actions,
    lastUpdate
  } = props;

  const {
    ready,
    items,
    onItemChange,
    isProxy
  } = usePhotoSwipeItems(props);

  const [showVideo, setShowVideo] = useState(false);
  const [currentItem, setCurrentItem] = useState({})

  const elementRef = useRef(null);
  const photoSwipeRef = useRef(null);

  const options = {
    //index, // deeplink into index or moment ID, not currently supported
    closeOnScroll: false,
    history: false,
    tapToToggleControls: false,
    fullscreenEl: true,
    closeEl: false,
    zoomEl: true,
    tapToClose: false,
    modal: false,
    focus: false
  };

  const destroy = () => {
    photoSwipeRef.current.close();
    photoSwipeRef.current = null;
  }

  const renderJsx = () => {
    let item = photoSwipeRef.current.currItem;

    if (item.jsx) {
      let container = document.getElementById(`pswp-jsx-${item.id}`);
      if (container && !container.dataset.rendered) {
        let jsx = <JsxContainer>{item.jsx}</JsxContainer>

        ReactDOM.render(jsx, container);
        container.dataset.rendered = 'true';
      }
    }
  }

  const playVideo = (item) => {
    setShowVideo(true);
  }

  const setup = () => {
    if (!ready) {
      return;
    }

    setCurrentItem(items[0]);

    photoSwipeRef.current = new PhotoSwipe(elementRef.current, PhotoSwipeUI_Default, items, options);
    photoSwipeRef.current.init();

    //photoSwipeRef.current.listen('loadComplete', renderJsx);

    photoSwipeRef.current.listen('afterChange', () => {
      //renderJsx();

      let index = photoSwipeRef.current.getCurrentIndex();
      setCurrentItem(photoSwipeRef.current.currItem);
      onItemChange(index);
    });

    renderJsx();

    // destroy on unmount
    return destroy;
  }

  useEffect(setup, [ready, lastUpdate]);

  if (!ready) {
    return null;
  }

  let counterStyle = null;
  if (props.hideCounter) {
    counterStyle = { display: 'none' };
  }

  let isVideo = false
  if (currentItem.is_video) {
    isVideo = true
  }

  return (
    <div>
      <div
        className="pswp"
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
        style={props.style}
        ref={elementRef}
      >
        <style>{'.pswp__top-bar {opacity: 1 !important; background: #222 !important}'}</style>
        <div className={`pswp__bg ${classes.pswpBg}`} />
        <div className="pswp__scroll-wrap">
          <div className="pswp__container">
            <div className="pswp__item" />
            <div className="pswp__item" />
            <div className="pswp__item" />
          </div>
          <div className="pswp__ui pswp__ui--hidden">
            <div className="pswp__top-bar">
              <div className="pswp__counter" style={counterStyle} />
              <button className="pswp__button pswp__button--close" title="Close (Esc)" />
              <button className="pswp__button pswp__button--fs" title="Toggle fullscreen" />
              <button className="pswp__button pswp__button--zoom" title="Zoom in/out" />
              {
                isVideo &&
                <button
                  key={playVideo}
                  className={`pswp__button ${classes.actionButton}`}
                  onClick={() => playVideo(photoSwipeRef.current?.currItem)}
                >
                  <PlayArrowIcon className={classes.actionIcon} />
                </button>
              }
              {actions && actions.map(action => ((
                <button
                  key={action.key}
                  className={`pswp__button ${classes.actionButton}`}
                  onClick={() => action.onClick(photoSwipeRef.current.currItem)}
                >
                  <action.icon className={classes.actionIcon} />
                </button>
              )))}
              <div className="pswp__preloader">
                <div className="pswp__preloader__icn">
                  <div className="pswp__preloader__cut">
                    <div className="pswp__preloader__donut" />
                  </div>
                </div>
              </div>
            </div>
            <div className="pswp__share-modal pswp__share-modal--hidden pswp__single-tap">
              <div className="pswp__share-tooltip" />
            </div>
            <button className="pswp__button pswp__button--arrow--left" title="Previous (arrow left)" />
            <button className="pswp__button pswp__button--arrow--right" title="Next (arrow right)" />
            <div className="pswp__caption">
              <div className="pswp__caption__center" />
            </div>
          </div>
        </div>
      </div>
      <VideoModal open={showVideo} src={photoSwipeRef.current?.currItem?.original} onClose={() => setShowVideo(false)} />
    </div>
  );
}

export default PhotoSwipeWrapper;
